/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DashboardHeader,
  LoanHeaderCard,
  PaymentCard,
  PaymentBottomCard,
  LoanDisbursementModal,
} from "../../components";
import { Signature, ChevronRight } from "../../assets/svgs";
import { ROUTES, PAYMENT_STATUS, EVENT_NAME } from "../../constants";
import {
  getPaymentCardDate,
  getPaymentCardStatus,
  getPaymentCardStyles,
  getPaymentCardIcon,
  getOrdinal,
  getInitials,
  convertTimestamp,
} from "../../utils";
import useApp from "../../hooks/useApp";
import useNavigateWithQuery from "../../hooks/useNavigateWithQuery";
import { logAnalyticEvent } from "../../analytics";
import "./styles.scss";

const FinanceDetail = () => {
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    selectedLanguage,
    userDetails,
    financeDetails,
    getFinanceApplications,
  } = useApp();
  const { financeApplicationId } = location.state || {};

  const [disbursementAmount, setDisbursementAmount] = useState(0);
  const [showLoanDisbursement, setShowLoanDisbursement] = useState(false);
  const [startTime, setStartTime] = useState(0);

  useEffect(() => {
    getFinanceApplications(financeApplicationId);

    setStartTime(Date.now());
    return () => {
      const endTime = Date.now();
      logAnalyticEvent(EVENT_NAME.PAGE_VIEW, {
        page_title: ROUTES.FINANCE_DETAIL,
        path: `${window.location.origin}/${ROUTES.FINANCE_DETAIL}`,
        time_spent: convertTimestamp(endTime - startTime),
      });
    };
  }, []);

  const pendingInstallments = financeDetails?.installments?.filter(
    installment => installment.status !== PAYMENT_STATUS.PAID
  );

  return (
    <div className="finance-detail-container">
      <DashboardHeader
        isFinanceDetail={true}
        isProgressBar={false}
        totalLoan={100}
        loanUtilization={financeDetails?.loan_utilization || 0}
        welcomBackHeading={`${t("global.hello")} ${userDetails?.first_name[selectedLanguage?.value]
          } 👋🏻`}
        loanAmountText={financeDetails?.formatted_finance_amount || 0}
        loanLabelText={t("finance-detail.loan_amount")}
        usersInitials={
          userDetails?.full_name &&
          getInitials(userDetails?.full_name[selectedLanguage?.value])
        }
        onClick={() => navigate(`/${ROUTES.SETTINGS}`)}

      />
      <LoanHeaderCard
        percentage={financeDetails?.loan_completion || 0}
        totalPaid={financeDetails?.formatted_total_paid_amount || 0}
        balanceRemaining={financeDetails?.formatted_remaining_amount || 0}
        availableToDisburse={financeDetails?.formatted_max_finance_amount || 0}
        onClick={() =>
          financeDetails?.id ? setShowLoanDisbursement(true) : null
        }
      />
      {pendingInstallments?.length ? (
        <div className="payment-card-wrapper">
          <div className="payment-card-header-container">
            <span className="payment-card-heading">
              {t("finance-detail.payments")}
            </span>
            <span
              className="payment-card-text-btn"
              onClick={() =>
                navigate(`/${ROUTES.PAYMENTS}`, {
                  state: { financeApplicationId },
                })
              }
            >
              {t("finance-detail.see_all")}
            </span>
          </div>
          {financeDetails?.installments
            ?.slice(0, 3)
            ?.map((installment, index) => {
              const date = getPaymentCardDate(installment.due_date);
              const status = getPaymentCardStatus({
                dueDateString: installment.due_date,
                translate: t,
              });
              const cardStyles = getPaymentCardStyles(status);
              const cardIcon = getPaymentCardIcon(status);
              const ordinal = getOrdinal(installment.installment_number);

              return (
                <PaymentCard
                  key={installment.id}
                  dataContainerBgColor={cardStyles.dataContainerBgColor}
                  statusContainerBgColor={cardStyles.statusContainerBgColor}
                  dayTextColor={cardStyles.dayTextColor}
                  monthTextColor={cardStyles.monthTextColor}
                  paymentStatusTextColor={cardStyles.paymentStatusTextColor}
                  dayText={date.day}
                  monthText={date.month}
                  paymentPeriodText={`${ordinal} ${t(
                    "finance-detail.payment"
                  )}`}
                  paymentAmountText={installment.formatted_amount_due}
                  icon={cardIcon}
                  paymentStatusText={status.label}
                  isLastItem={
                    index === financeDetails?.installments?.length - 1
                  }
                  isRounded={false}
                />
              );
            })}
        </div>
      ) : null}
      <PaymentBottomCard
        icon={Signature}
        arrowIcon={ChevronRight}
        buttonText={t("finance-detail.loan_agreement")}
      />
      <LoanDisbursementModal
        visible={showLoanDisbursement}
        buttonText={t("finance-detail.request_loan_disburse_btn_text")}
        value={disbursementAmount}
        onChange={value => setDisbursementAmount(value)}
        onClickButton={() => setShowLoanDisbursement(false)}
        onClickClose={() => setShowLoanDisbursement(false)}
      />
    </div>
  );
};

export default FinanceDetail;
